/* eslint-disable max-lines */
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CardMedia from '@material-ui/core/CardMedia';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Toolbar from 'components/Layout/Toolbar';
import ErrorMessage from 'components/Shared/ErrorMessage';
import { decimal, document, phone, zipcode } from 'formatters/masks';
import { IVenture } from 'interfaces/venture';
import React, { Fragment, memo, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable, useRetryableObservable } from 'react-use-observable';
import RxOp from 'rxjs-operators';
import mapService from 'services/map';
import selectedVentureService, { ISelection } from 'services/profile';
import ventureService from 'services/venture';

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  column: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  subTitles: {
    fontSize: theme.spacing(2),
    color: theme.palette.primary.main,
    margin: '4px 0',
    fontWeight: 700,
  },
  media: {
    height: 0,
    paddingTop: '15%', // 16:9
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      height: theme.spacing(5),
      width: theme.spacing(5)
    }
  },
  maps: {
    height: 0,
    paddingTop: '71.5%',
    backgroundColor: '#F7F5E5',
    borderRadius: theme.spacing(2)
  },
  container: {
    '& input' : {
      color: theme.palette.text.primary+' !important'
    }
  },
  title: {
    fontWeight: 700,
  }
}));

const VentureView = () => {
  const classes = useStyles();
  const history = useHistory();
  const [googleMapImage, setGoogleMapImage] = useState('');

  const formatDate = (date: Date) => {
    date = new Date(date);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day > 9 ? '' : '0'}${day}/${month > 9 ? '' : '0'}${month}/${year}`;
  };

  const [selectedVenture] = useObservable<ISelection>(() => {
    return selectedVentureService.getSelectedVenture().pipe(
      RxOp.logError(),
      RxOp.map((selectedVenture) => {
        if (!selectedVenture) return history.push('/');
        return selectedVenture;
      })
    );
  }, []);

  const [venture, error, loading, loadData] = useRetryableObservable<IVenture>(() => {
    return ventureService.getById(selectedVenture.id);
  }, [selectedVenture]);

  const fatchMap = useCallback(() => {
    if (venture && venture.address) {
      mapService.getStaticMapImage(venture.address).then((image) => {
        setGoogleMapImage(image);
      }).catch((error) => {
        console.log(error);
        setGoogleMapImage('');
      });
    }
    setGoogleMapImage('');
  }, [setGoogleMapImage, venture]);

  useEffect(() => {
    fatchMap();
  }, [fatchMap, venture]);

  return (
    <Fragment>
      <Toolbar title='Ficha Técnica' breadcrumbs={[{ title: 'Ficha Técnica' }]} />
      <Container  className={classes.container}>
        <Card>
          {loading && <LinearProgress color='secondary' />}
          {error && (<ErrorMessage error={error} tryAgain={loadData} />)}
          {!error && venture && (
            <>
              <CardHeader
                classes={{ title: classes.title }}
                title={venture.name}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography className={classes.subTitles}>Informações</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label='Nome'
                          disabled
                          value={venture.name || '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label='CNPJ'
                          disabled
                          value={venture.cnpj ? document(venture.cnpj.toString()) : '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          label="Razão Social"
                          disabled
                          value={venture.socialReason || '-'}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          disabled
                          label='Data de Inauguração'
                          value={venture.inauguration ? formatDate(venture.inauguration) : '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          disabled
                          label='Telefone/Contato'
                          value={venture.telphone ? phone(venture.telphone.toString()) : '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          label="Site"
                          disabled
                          value={venture.site || '-'}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={classes.subTitles}>Lojas</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4} lg={2}>
                        <TextField
                          fullWidth
                          disabled
                          label='Lojas'
                          value={venture.stores || '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4} lg={2}>
                        <TextField
                          fullWidth
                          disabled
                          label='Mega Lojas'
                          value={venture.megaStores || '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4} lg={2}>
                        <TextField
                          fullWidth
                          disabled
                          label='Lojas Âncoras'
                          value={venture.store || '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='Cinema (salas)'
                          value={venture.movieTheaters || '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='Vagas de estacionamento'
                          value={venture.parking || '-'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={classes.subTitles}>Áreas (metragens)</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={4} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='Área do Terreno m²'
                          value={venture.groundArea ? decimal.apply(venture.groundArea) : '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={4} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='Área Construída m²'
                          value={venture.buildingArea ? decimal.apply(venture.buildingArea) : '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={2} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='ABL m²'
                          value={venture.abl ? decimal.apply(venture.abl) : '-'}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={2} lg={3}>
                        <TextField
                          fullWidth
                          disabled
                          label='ABC m²'
                          value={venture.abc ? decimal.apply(venture.abc) : '-'}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography className={classes.subTitles}>Localização</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <CardMedia
                          className={classes.maps}
                          image={googleMapImage}
                          title="Mapa do empreendimento"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          disabled
                          label='Endereço'
                          value={venture.address || '-'}
                        />
                      </Grid>
                      <Grid item xs={4} sm={4} md={6} lg={4}>
                        <TextField
                          fullWidth
                          disabled
                          label='UF'
                          value={venture.uf || '-'}
                        />
                      </Grid>
                      <Grid item xs={8} sm={4} md={6} lg={4}>
                        <TextField
                          fullWidth
                          disabled
                          label='Cidade'
                          value={venture.city || '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={12} lg={4}>
                        <TextField
                          fullWidth
                          disabled
                          label='CEP'
                          value={venture.cep ? zipcode(venture.cep) : '-'}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </>
          )}
        </Card>
      </Container>
    </Fragment>
  );
};

export default memo(VentureView);